import { Block, OfferItemsUI, OfferUI } from '../types/ProductPagePlacement';
import { applyAllDiscountCodes } from './discounts-ppp';
import updateDrawerCarts, { isFunction } from './update-drawer-cart';
import { logInfo } from '../utils/logging';
import { PPP_CHECKBOX_LAYOUT_OFFERS_ADDED_TO_CART_EVENT } from '../constants/app';
import { sendOfferAddedToCartForPPP } from './tracking-utils-ppp';
import { ShopifyAddToCartResponse } from '../api/types';

const FETCH_HEADERS = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

/**
 * Validates a Shopify add-to-cart response and throws an error if it contains an error status.
 * Shopify's /cart/add.js returns HTTP 200 even for errors, with error details in JSON body.
 * We must check for the 'status' field to detect failures and prevent subsequent operations
 * (discounts, tracking, cart drawer updates) from running when items weren't actually added.
 *
 * @param data - The response data from Shopify's /cart/add.js endpoint
 * @throws {Error} If the response contains a status field indicating an error
 */
function validateAddToCartResponse(
  data: ShopifyAddToCartResponse,
): asserts data is Record<string, unknown> {
  if (data && typeof data === 'object' && 'status' in data) {
    const errorMessage =
      'message' in data && typeof data.message === 'string'
        ? data.message
        : `Add to cart failed with status ${data.status}`;
    throw new Error(errorMessage);
  }
}

export type PPPSelectedOffers = Record<
  string,
  {
    items: Record<string, OfferItemsUI>;
    data: OfferUI;
    block: Block;
  }
>;

/**
 * If any block is using ATC store button we allow the button to call loadProductPageExtension()
 * otherwise we proceed as normal.
 */
export const isProductPageExtensionStoreButtonActive = (): boolean => {
  const productPagePlacementBlocks = window.CandyRackAppBlocks?.blocks || {};

  const hasProductPagePlacementBlocks =
    Object.keys(productPagePlacementBlocks)?.length > 0;

  const blocksWithATCStoreButton = Object.keys(
    productPagePlacementBlocks,
  ).filter((blockKey: string) => {
    const block = productPagePlacementBlocks[blockKey] as Block;
    return block.settings.button_location === 'store_button';
  });

  const isUsingATCStoreButton = blocksWithATCStoreButton.length > 0;

  logInfo('[PPP] Using ATC store button: ' + isUsingATCStoreButton);
  return hasProductPagePlacementBlocks && isUsingATCStoreButton;
};

const triggerCustomFunctions = async (isPPPInitiatedAddToCart = false) => {
  // handle the action after adding the products to the cart + tracker + discounts
  // Note: Please keep this logic in the end of the function after everything is applied successfully
  if (isPPPInitiatedAddToCart) {
    // update the cart drawer
    const updatedCart = await fetch('/cart.js');
    const updatedCartJSON = await updatedCart.json();
    updateDrawerCarts(updatedCartJSON);
  }

  // use in the checkbox layout with ATC app button
  if (window.CANDYRACK_PPP_ATC_APP_BUTTON_UPDATE_CART_REDIRECT_TO_CART) {
    window.location.href = '/cart';
    return;
  }

  // use in button layout ATC button
  if (window.CANDYRACK_PPP_ATC_BUTTON_LAYOUT_REDIRECT_TO_CART) {
    window.location.href = '/cart';
    return;
  }

  if (isFunction(window.CANDYRACK_PPP_ATC_APP_BUTTON_CALLBACK)) {
    const updatedCart = await fetch('/cart.js');
    const updatedCartJSON = await updatedCart.json();
    window.CANDYRACK_PPP_ATC_APP_BUTTON_CALLBACK(updatedCartJSON);
    return;
  }

  if (isFunction(window.CANDYRACK_PPP_ATC_BUTTON_LAYOUT_CALLBACK)) {
    const updatedCart = await fetch('/cart.js');
    const updatedCartJSON = await updatedCart.json();
    window.CANDYRACK_PPP_ATC_BUTTON_LAYOUT_CALLBACK(updatedCartJSON);
    return;
  }
};

export const addToCartButtonLayout = async ({
  isPPPInitiatedAddToCart,
  blockId = '',
  variantId,
  storefrontAccessToken,
}: {
  isPPPInitiatedAddToCart: boolean;
  blockId: string;
  variantId: number;
  storefrontAccessToken: string;
}) => {
  const productPagePlacementBlocks =
    (window.CandyRackAppBlocks?.blocks as Record<string, Block>) || {};

  const currentBlock = productPagePlacementBlocks[blockId];
  const selectedOffers = getSelectedOffers({
    [blockId]: currentBlock,
  });

  const hasOffers = Object.keys(selectedOffers).length > 0;
  if (!hasOffers) {
    console.log('[PPP] No offers available. Exiting...');
    return;
  }

  const offerItems = prepareSelectedOfferItemsForCart(selectedOffers);
  const selectedOfferItems = offerItems.filter(
    (offerItem) => offerItem.id === variantId,
  );

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: FETCH_HEADERS,
      body: JSON.stringify({ items: selectedOfferItems }),
    });
    const data = (await response.json()) as ShopifyAddToCartResponse;
    validateAddToCartResponse(data);

    // Always update attributes for slider cart, but only track if add to cart was successful
    await sendOfferAddedToCartForPPP(
      {
        [blockId]: currentBlock,
      },
      isPPPInitiatedAddToCart,
      variantId,
      data,
    );

    // handle discounts
    await applyAllDiscountCodes(
      selectedOffers,
      !isPPPInitiatedAddToCart,
      storefrontAccessToken,
    );

    // custom functions dealing mainly with cart drawer + counter updates
    await triggerCustomFunctions(isPPPInitiatedAddToCart);
    return Promise.resolve(true);
  } catch (error) {
    console.error('error adding the products to the cart: ' + error);
    return Promise.reject(error);
  }
};

/*
 * The PPP Checkbox Layout add selection to cart button must be clicked
 */
export const loadProductPageExtensionForCheckboxLayoutAppButton = async (
  storefrontAccessToken: string,
) => {
  return await loadProductPageExtensionForCheckboxLayout(
    true,
    storefrontAccessToken,
  );
};

/*
 * The typical ATC button of the store must be clicked while the Checkbox Layout exists
 */
export const loadProductPageExtensionForCheckboxLayoutStoreButton = async (
  storefrontAccessToken: string,
) => {
  return await loadProductPageExtensionForCheckboxLayout(
    false,
    storefrontAccessToken,
  );
};

/**
 *
 * @param isPPPInitiatedAddToCart:
 *    @If isPPPInitiatedAddToCart = true: Checkbox layout with App ATC for PPP exists and the PPP button gets triggered
 *    @If isPPPInitiatedAddToCart = false: Checkbox layout with store button exists and we trigger the store ATC
 * @param products
 *    In the case of Button layout is a single product, in the case of checkbox layout can be multiple
 * @returns
 */
const loadProductPageExtensionForCheckboxLayout = async (
  isPPPInitiatedAddToCart: boolean,
  storefrontAccessToken: string,
) => {
  const checkboxLayoutBlocks = Object.entries(
    (window.CandyRackAppBlocks?.blocks as Record<string, Block>) || {},
  ).reduce((acc, [blockId, block]) => {
    if (block.blockLayout === 'checkbox') {
      acc[blockId] = block;
    }
    return acc;
  }, {} as Record<string, Block>);

  if (Object.keys(checkboxLayoutBlocks).length === 0) {
    console.log('[PPP] No checkbox layout blocks found. Exiting...');
    return;
  }

  const selectedOffers = getSelectedOffers(checkboxLayoutBlocks);

  const hasOffers = Object.keys(selectedOffers).length > 0;
  if (!hasOffers) {
    console.log('[PPP] No offers available. Exiting...');
    return;
  }

  const selectedOfferItems = prepareSelectedOfferItemsForCart(selectedOffers);

  try {
    const response = await fetch(
      `/cart/add.js${isPPPInitiatedAddToCart ? '' : '?sliderCartOpen=false'}`,
      {
        method: 'POST',
        headers: FETCH_HEADERS,
        body: JSON.stringify({ items: selectedOfferItems }),
      },
    );
    const data = (await response.json()) as ShopifyAddToCartResponse;
    validateAddToCartResponse(data);

    window.CandyRackAppBlocks!.variantIdsInCart = selectedOfferItems.map(
      (offerItem) => offerItem.id,
    );

    // Always update attributes for slider cart, but only track if add to cart was successful
    void sendOfferAddedToCartForPPP(
      checkboxLayoutBlocks,
      isPPPInitiatedAddToCart,
      null,
      data,
    );

    // Reset selected offers via dispatching a custom event
    document.dispatchEvent(
      new CustomEvent(PPP_CHECKBOX_LAYOUT_OFFERS_ADDED_TO_CART_EVENT),
    );
    logInfo(
      `[PPP] event ${PPP_CHECKBOX_LAYOUT_OFFERS_ADDED_TO_CART_EVENT} dispatched`,
    );

    // handle discounts
    await applyAllDiscountCodes(
      selectedOffers,
      !isPPPInitiatedAddToCart,
      storefrontAccessToken,
    );

    // custom functions dealing mainly with cart drawer + counter updates
    await triggerCustomFunctions(isPPPInitiatedAddToCart);

    return Promise.resolve(true);
  } catch (error) {
    console.error('error adding the products to the cart: ' + error);
    return Promise.reject(error);
  }
};

export type AddToCartItems = {
  id: number;
  quantity: number;
  properties: {
    note: string;
  };
}[];

export const prepareSelectedOfferItemsForCart = (
  selectedOffers: PPPSelectedOffers,
) => {
  const addToCartItems: AddToCartItems = [];
  Object.keys(selectedOffers).forEach((offerId) => {
    const offer = selectedOffers[offerId];
    Object.keys(offer.items).forEach((offerItemId) => {
      const offerItem = offer.items[offerItemId];
      if (
        offerItem &&
        offerItem.selectedVariantId &&
        offerItem.selectedVariantQuantity
      ) {
        addToCartItems.push({
          id: offerItem.selectedVariantId,
          quantity: offerItem.selectedVariantQuantity,
          properties: {
            note: offerItem.note,
          },
        });
      }
    });
  });

  return addToCartItems;
};

// we are using 2 nested maps to looked easily the selected offer and its items and to avoid duplicated
// when we have more than 2 blocks in the same page
// Nested map = { offerId: { items: {itemId: offerItem }, data: {}} }
export const getSelectedOffers = (
  productPagePlacementBlocks: Record<string, Block>,
): PPPSelectedOffers => {
  let selectedOffersNestedMap: PPPSelectedOffers = {};

  Object.keys(productPagePlacementBlocks).forEach((blockKey) => {
    const block = productPagePlacementBlocks[blockKey];
    const blockOffers = block.offers;
    blockOffers?.forEach((offer) => {
      offer.items.forEach((offerItem) => {
        if (offerItem.isSelected) {
          selectedOffersNestedMap = {
            ...selectedOffersNestedMap,
            [offer.id]: {
              data: offer,
              items: {
                ...selectedOffersNestedMap[offer.id]?.items,
                [offerItem.itemId]: offerItem,
              },
              block,
            },
          };
        }
      });
    });
  });

  return selectedOffersNestedMap;
};
